/**
 * AR Collections — Collector performance panel + AI weekly summary.
 * Extracted from FinanceCollections.jsx in Iter 82.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RefreshCw, Sparkles, Trophy, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "./constants";
import { apiErrorMessage } from "@/lib/apiError";

function StatCard({ label, value, tone = "", testid }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono mt-1 ${tone}`} data-testid={testid}>{value}</div>
    </CardContent></Card>
  );
}

export default function CollectorPerformancePanel({ wsId }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/finance/workspaces/${wsId}/ar/collector-performance?days=${days}`);
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setLoading(false); }
  }, [wsId, days]);

  useEffect(() => { load(); }, [load]);

  const runSummary = async () => {
    setAiBusy(true); setAiSummary(null);
    try {
      const { data: d } = await api.post(`/finance/workspaces/${wsId}/ar/collector-performance/ai-summary?days=${days}`);
      setAiSummary(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="collector-perf-panel">
      <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Window (days)</Label>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40" data-testid="perf-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="perf-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
        <Button onClick={runSummary} disabled={aiBusy} data-testid="perf-ai-summary">
          {aiBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {aiBusy ? "Generating…" : "AI weekly summary"}
        </Button>
      </CardContent></Card>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <StatCard label="Team recovered" value={fmt(data.totals.amount_recovered)} tone="text-emerald-600" testid="perf-recovered" />
          <StatCard label="At risk touched" value={fmt(data.totals.amount_at_risk)} tone="text-rose-600" testid="perf-at-risk" />
          <StatCard label="Reminders sent" value={data.totals.reminders_sent} testid="perf-sent" />
          <StatCard label="Customers worked" value={data.totals.customers_worked} testid="perf-customers" />
        </div>
      )}

      {aiSummary && (
        <Card className="border-primary/30 bg-primary/[0.03]"><CardContent className="p-4 space-y-2" data-testid="perf-ai-card">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <div className="font-semibold" data-testid="perf-ai-headline">{aiSummary.ai?.headline}</div>
          </div>
          <div className="text-sm whitespace-pre-wrap" data-testid="perf-ai-narrative">{aiSummary.ai?.narrative}</div>
          {aiSummary.ai?.top_performer && (
            <div className="text-sm border rounded p-2 bg-emerald-500/10">
              <b>Top performer:</b> {aiSummary.ai.top_performer.user_name} — {aiSummary.ai.top_performer.why}
            </div>
          )}
          {aiSummary.ai?.coach_recommendations?.length > 0 && (
            <div className="text-sm">
              <div className="font-medium text-xs uppercase text-muted-foreground mb-1">Coaching suggestions</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {aiSummary.ai.coach_recommendations.map((c, i) => (
                  <li key={i}><b>{c.user_name}</b>: {c.focus}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            <TrendingUp className="w-3 h-3 inline mr-1" />
            Trend: <b className={aiSummary.ai?.team_trend === "positive" ? "text-emerald-600" : aiSummary.ai?.team_trend === "concerning" ? "text-rose-600" : "text-muted-foreground"}>{aiSummary.ai?.team_trend}</b>
          </div>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="perf-table">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Collector</th>
                <th className="text-right px-4 py-3">Reminders</th>
                <th className="text-right px-4 py-3">Customers</th>
                <th className="text-right px-4 py-3">Recovered</th>
                <th className="text-right px-4 py-3">Recovery %</th>
                <th className="text-right px-4 py-3">Promise-keep %</th>
                <th className="text-right px-4 py-3">Avg days</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</td></tr>}
              {!loading && (!data?.rows || data.rows.length === 0) && (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="perf-empty">
                  No collections activity in this window.
                </td></tr>
              )}
              {(data?.rows || []).map((r) => (
                <tr key={r.user_id} className="border-t hover:bg-muted/20" data-testid={`perf-row-${r.user_id}`}>
                  <td className="px-4 py-3 font-medium">{r.user_name}</td>
                  <td className="px-4 py-3 text-right">{r.reminders_sent}</td>
                  <td className="px-4 py-3 text-right">{r.customers_worked}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-600">{fmt(r.amount_recovered)}</td>
                  <td className="px-4 py-3 text-right">{r.recovery_rate != null ? `${r.recovery_rate}%` : "—"}</td>
                  <td className="px-4 py-3 text-right">{r.promise_keep_rate != null ? `${r.promise_keep_rate}%` : "—"}</td>
                  <td className="px-4 py-3 text-right">{r.avg_days_to_collect != null ? `${r.avg_days_to_collect}d` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}
